import useIsMobile from 'hooks/useIsMobile'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FolderPlus, Menu, Plus, Trash2 } from 'react-feather'
import { Button, Input } from 'reactstrap'
import colors from 'Theme/colors'
import {
  OPERATOR_JOIN_OPTIONS,
  OPERATOR_OP_OPTIONS,
  generateHumanReadableSummary,
} from './schemaRuleUtils'

// Single grouped list of materials: rows live inside groups, operators sit on the bracket
const SchemaRuleBuilder = ({
  rule,
  onChangeRule,
  inputs,
  rulePayload,
  getItemLabel,
  renderRow,
  onAddInput,
  onAddGroup,
  onRemoveGroup,
  onMoveInput,
}) => {
  const isMobile = useIsMobile()
  const groups = rule.groups
  // Native HTML5 drag works with a mouse only; touch users keep the "Kelompok n" picker on each row
  const canDrag = groups.length > 1 && !isMobile
  const [draggingIndex, setDraggingIndex] = useState(null)
  const [dropGroupId, setDropGroupId] = useState(null)
  const draggingFrom = draggingIndex === null ? null : inputs[draggingIndex]?.groupId

  const endDrag = () => {
    setDraggingIndex(null)
    setDropGroupId(null)
  }

  const dropZoneProps = (groupId) =>
    canDrag
      ? {
          onDragOver: (e) => {
            if (draggingIndex === null || draggingFrom === groupId) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setDropGroupId(groupId)
          },
          onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDropGroupId(null)
          },
          onDrop: (e) => {
            e.preventDefault()
            if (draggingIndex !== null && draggingFrom !== groupId) onMoveInput(draggingIndex, groupId)
            endDrag()
          },
        }
      : {}
  const summary = generateHumanReadableSummary(rulePayload, getItemLabel)

  const handleGroupOpChange = (groupId, op) =>
    onChangeRule({
      ...rule,
      groups: groups.map((g) => (g.id === groupId ? { ...g, op } : g)),
    })

  // Operator pill sitting on a "[" bracket that spans its children, query-builder style
  const renderBracket = ({ show, value, options, onChange, testId, children }) => {
    const opt = options.find((o) => o.value === value) || options[0]
    if (!show) return <div className="d-flex flex-column gap-2">{children}</div>
    const pill = (
      <Input
        type="select"
        bsSize="sm"
        value={opt.value}
        onChange={(e) => onChange(e.target.value)}
        title={opt.description}
        aria-label="Operator logika"
        className="rounded-pill fw-semibold"
        style={{
          width: '82px',
          fontSize: '11px',
          padding: '2px 20px 2px 10px',
          backgroundPosition: 'right 6px center',
          color: colors.primary[400],
          background: colors.primary[25],
          borderColor: colors.primary[100],
        }}
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.pill}
          </option>
        ))}
      </Input>
    )
    // Mobile: no room for a side bracket, so the pill heads a thin left rail instead
    if (isMobile) {
      return (
        <div className="d-flex flex-column gap-2">
          {pill}
          <div
            className="d-flex flex-column gap-2 ps-2"
            style={{ borderLeft: `2px solid ${colors.neutral[300]}` }}
          >
            {children}
          </div>
        </div>
      )
    }
    return (
      <div className="position-relative d-flex flex-column gap-2" style={{ paddingLeft: '76px' }}>
        <div
          style={{
            position: 'absolute',
            left: '40px',
            top: '22px',
            bottom: '22px',
            width: '26px',
            border: `1.5px solid ${colors.neutral[300]}`,
            borderRight: 'none',
            borderRadius: '6px 0 0 6px',
          }}
        />
        <div
          style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}
        >
          {pill}
        </div>
        {children}
      </div>
    )
  }

  const renderRows = (group, groupIdx) => {
    const rows = inputs.filter((inp) => inp.groupId === group.id)
    const hasBracket = rows.length > 1
    return (
      <>
        {renderBracket({
          show: hasBracket,
          value: group.op,
          options: OPERATOR_OP_OPTIONS,
          onChange: (op) => handleGroupOpChange(group.id, op),
          testId: `schema-rule-group-op-${groupIdx}`,
          children: rows.map((row) => (
            <div
              key={row.i}
              className={`d-flex align-items-stretch flex-wrap gap-2 ${isMobile ? 'border rounded-2 bg-white p-2' : ''}`}
              style={{ opacity: draggingIndex === row.i ? 0.4 : 1 }}
            >
              {canDrag && (
                <span
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', String(row.i)) // Firefox needs data to start a drag
                    e.dataTransfer.setDragImage(e.currentTarget.parentElement, 16, 16)
                    setDraggingIndex(row.i)
                  }}
                  onDragEnd={endDrag}
                  title="Seret ke kelompok lain"
                  aria-hidden="true"
                  className="d-flex align-items-center text-muted"
                  style={{ cursor: 'grab' }}
                  data-testid={`schema-rule-drag-handle-${row.i}`}
                >
                  <Menu size={14} />
                </span>
              )}
              {renderRow(row)}
            </div>
          )),
        })}
        {/* Per-group add only when there are several groups; a single group uses the header button */}
        {groups.length > 1 && (
          <div style={{ paddingLeft: hasBracket && !isMobile ? '76px' : 0 }}>
            <Button
              color="link"
              size="sm"
              className="p-0 mt-2 d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold"
              onClick={() => onAddInput(group.id)}
              data-testid={`schema-rule-add-item-${groupIdx}`}
            >
              <Plus size={14} />
              Tambah bahan
            </Button>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="d-flex flex-column gap-3" data-testid="schema-rule-builder-container">
      <div className="text-muted" style={{ fontSize: '12px' }}>
        Dan = semua wajib · Atau = minimal 1 · Salah satu = tepat 1 (substitusi)
      </div>

      <div className={`border rounded-3 bg-white ${isMobile ? 'p-2' : 'p-3'}`}>
        {groups.length === 1
          ? renderRows(groups[0], 0)
          : renderBracket({
              show: true,
              value: rule.join,
              options: OPERATOR_JOIN_OPTIONS,
              onChange: (join) => onChangeRule({ ...rule, join }),
              testId: 'schema-rule-join-select',
              children: groups.map((group, groupIdx) => (
                <div
                  key={group.id}
                  className="border rounded-3 p-2 ps-3"
                  style={
                    dropGroupId === group.id
                      ? { background: colors.primary[25], border: `1.5px dashed ${colors.primary[300]}` }
                      : { background: colors.neutral[25] }
                  }
                  {...dropZoneProps(group.id)}
                  data-testid={`schema-rule-group-${groupIdx}`}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-semibold text-muted" style={{ fontSize: '12px' }}>
                      Kelompok {groupIdx + 1}
                    </span>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      className="py-0 px-1 border-0"
                      onClick={() => onRemoveGroup(group.id)}
                      title="Hapus kelompok (bahan dipindah ke kelompok lain)"
                      aria-label="Hapus kelompok"
                      data-testid={`schema-rule-remove-group-${groupIdx}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  {renderRows(group, groupIdx)}
                </div>
              )),
            })}

        <div className={`mt-3 pt-3 border-top ${isMobile ? 'text-center' : ''}`}>
          <Button
            color="link"
            size="sm"
            className="p-0 d-inline-flex align-items-center gap-2 text-decoration-none fw-semibold"
            onClick={onAddGroup}
            title="Buat kelompok baru untuk aturan Dan / Atau / Salah satu antar kelompok"
            data-testid="schema-rule-add-group-btn"
          >
            <FolderPlus size={15} />
            Tambah Kelompok
          </Button>
        </div>
      </div>

      <div
        className="p-3 rounded-3 small"
        style={{ background: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}
      >
        <span className="fw-semibold text-muted me-2">Pratinjau Skema:</span>
        <span className="fw-bold text-primary" style={{ fontSize: '13px' }}>
          {summary.formula}
        </span>
        <div className="text-muted" style={{ fontSize: '12px' }}>
          {summary.explanation}
        </div>
      </div>
    </div>
  )
}

SchemaRuleBuilder.propTypes = {
  rule: PropTypes.shape({
    join: PropTypes.string,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        op: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  onChangeRule: PropTypes.func.isRequired,
  inputs: PropTypes.arrayOf(PropTypes.object).isRequired,
  rulePayload: PropTypes.object.isRequired,
  getItemLabel: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired,
  onAddInput: PropTypes.func.isRequired,
  onAddGroup: PropTypes.func.isRequired,
  onRemoveGroup: PropTypes.func.isRequired,
  onMoveInput: PropTypes.func.isRequired,
}

export default SchemaRuleBuilder
